import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  CLARI_CHAT_MODEL_VENDOR_BY_ID,
  getClariChatModelsFlat
} from 'src/app/shared/clari-chat-model-catalog';
import { Deepseekv3Component } from '../dialogs/deepseekv3/deepseekv3.component';
import { OpusFourComponent } from '../dialogs/opus-four/opus-four.component';
import { OpusSixComponent } from '../dialogs/opus-six/opus-six.component';
import { PalmyraComponent } from '../dialogs/palmyra/palmyra.component';
import { JambaComponent } from '../dialogs/jamba/jamba.component';
import { MistralLargeComponent } from '../dialogs/mistral-large/mistral-large.component';
import { GptFiveComponent } from '../dialogs/gpt-five/gpt-five.component';
import { GptFiveMiniComponent } from '../dialogs/gpt-five-mini/gpt-five-mini.component';
import { MaverickComponent } from '../dialogs/maverick/maverick.component';
import { ScoutComponent } from '../dialogs/scout/scout.component';
import { CoherecommandrComponent } from '../dialogs/coherecommandr/coherecommandr.component';
import { DeepseekComponent } from '../dialogs/deepseek/deepseek.component';
import { CohereRPlusComponent } from '../dialogs/cohere-r-plus/cohere-r-plus.component';
import { Googleveo31Component } from '../dialogs/googleveo31/googleveo31.component';
import { StabilityComponent } from '../dialogs/stability/stability.component';
import { CogxComponent } from '../dialogs/cogx/cogx.component';
import { GoogleImageComponent } from '../dialogs/google-image/google-image.component';
import { DalEComponent } from '../dialogs/dal-e/dal-e.component';
import { PixtralImageComponent } from '../dialogs/pixtral-image/pixtral-image.component';
import { SonnetFourComponent } from '../dialogs/sonnet-four/sonnet-four.component';
import { HaikuComponent } from '../dialogs/haiku/haiku.component';
import { GlmComponent } from '../dialogs/glm/glm.component';
import { ModelBioDetailDialogComponent } from '../dialogs/model-bio-detail-dialog/model-bio-detail-dialog.component';


interface GardenModel {
  name: string;
  value: string;
  provider: string;
  image: string;
  blurb: string;
}

interface GardenCategory {
  id: string;
  title: string;
  subtitle: string;
  models: GardenModel[];
}

@Component({
  selector: 'app-model-garden',
  templateUrl: './model-garden.component.html',
  styleUrls: ['./model-garden.component.scss']
})
export class ModelGardenComponent {
  private readonly modelDialogMap: Record<string, any> = {
    deepv3: Deepseekv3Component,
    opus46: OpusFourComponent,
    opus47: OpusSixComponent,
    opus6: OpusSixComponent,
    palmx: PalmyraComponent,
    palmyra: PalmyraComponent,
    jamba: JambaComponent,
    mistrallarge: MistralLargeComponent,
    codestralto: MistralLargeComponent,
    chatgpt54: GptFiveComponent,
    minichatgpt54: GptFiveMiniComponent,
    llama: MaverickComponent,
    maverick: MaverickComponent,
    scoutinstruct: ScoutComponent,
    scout: ScoutComponent,
    commandrinstruct: CoherecommandrComponent,
    deepseek: DeepseekComponent,
    cohere: CohereRPlusComponent,
    veo31: Googleveo31Component,
    novareel11: Googleveo31Component,
    stability35l: StabilityComponent,
    cogx: CogxComponent,
    geminiproimage: GoogleImageComponent,
    dale: DalEComponent,
    pixtralimage: PixtralImageComponent,
    glm5: GlmComponent,
    sonnet45: SonnetFourComponent,
    haiku45: HaikuComponent
  };

  readonly categoryCardsPerView = 4;
  readonly categories: GardenCategory[] = [
    {
      id: 'reasoning-vision',
      title: 'Reasoning Models (Vision)',
      subtitle: 'Visual-capable reasoning models for image understanding and multimodal analysis.',
      models: [
        { name: 'Claude Sonnet 4.5', value: 'sonnet45', provider: 'Anthropic', image: 'assets/logos/anthropic.svg', blurb: 'Claude Sonnet 4.5 balances strong vision-language reasoning, coding support, and instruction fidelity for complex multimodal tasks.' },

        { name: 'Pixtral', value: 'pixtralimage', provider: 'Mistral', image: 'assets/logos/mistral.svg', blurb: 'Pixtral is built for high-quality vision-language reasoning, with strong performance in captioning, visual Q&A, and image-driven interpretation.' }
        // { name: 'GLM-4.6V', value: 'glm46v', provider: 'Z.ai', image: 'assets/logos/zai.svg', blurb: 'GLM-4.6V is designed for multimodal reasoning with a strong balance of visual understanding and multilingual inference capabilities.' }
      ]
    },
    {
      id: 'reasoning-text',
      title: 'Reasoning Models (Text)',
      subtitle: 'Text-first reasoning models for analysis, planning, coding, and enterprise workflows.',
      models: [
        { name: 'DeepSeek V3.2', value: 'deepv3', provider: 'DeepSeek', image: 'assets/logos/deepseek.svg', blurb: 'DeepSeek V3.2 offers high-quality text generation and strong reasoning depth with efficient performance for production workflows.' },
        { name: 'Claude Opus 4.6', value: 'opus46', provider: 'Anthropic', image: 'assets/logos/anthropic.svg', blurb: 'Claude Opus 4.6 advances long-context reasoning and coding reliability for high-stakes enterprise analysis, synthesis, and strategic planning workflows.' },
        { name: 'Writer X5', value: 'palmyra', provider: 'Writer', image: 'assets/logos/writer.png', blurb: 'Writer X5 is Writer’s enterprise reasoning model tuned for business writing, policy control, and high-reliability document workflows.' },
        { name: 'Codestral', value: 'codestralto', provider: 'Mistral AI', image: 'assets/logos/mistral.svg', blurb: 'Codestral is Mistral’s code-first model for implementation, refactors, reviews, and developer-focused explanations with structured output.' },
        { name: 'Llama 4', value: 'llama', provider: 'Meta', image: 'assets/logos/meta.svg', blurb: 'Llama 4 is Meta’s latest open model family focused on strong general reasoning and flexible deployment options.' }
      ]
    },
    {
      id: 'instruct',
      title: 'Instruct Models',
      subtitle: 'Instruction-tuned models for direct task completion and enterprise chat.',
      models: [
        { name: 'Claude Haiku 4.5', value: 'haiku45', provider: 'Anthropic', image: 'assets/logos/anthropic.svg', blurb: 'Claude Haiku 4.5 is optimized for fast multimodal reasoning and concise, reliable visual-text responses in day-to-day production workflows.' },
        { name: 'Meta Scout Instruct', value: 'scoutInstruct', provider: 'Meta', image: 'assets/logos/meta.svg', blurb: 'Fast instruction-tuned model for low-latency enterprise chat workflows.' },
        { name: 'Cohere Command R Instruct', value: 'commandRInstruct', provider: 'Cohere', image: 'assets/logos/cohere.svg', blurb: 'Instruction-focused model for grounded, business-friendly responses.' },
        { name: 'Jamba Large', value: 'jamba', provider: 'AI21', image: 'assets/logos/ai21.png', blurb: 'Jamba Large combines long-context performance and efficient inference for scalable enterprise reasoning use cases.' },
        { name: 'Mistral Large', value: 'mistrallarge', provider: 'Mistral AI', image: 'assets/logos/mistral.svg', blurb: 'Mistral Large is a fast, code-friendly model with strong reasoning clarity for real-time assistant and analysis tasks.' },
        { name: 'DeepSeek R1', value: 'deepseek', provider: 'DeepSeek', image: 'assets/logos/deepseek.svg', blurb: 'Deepseek R1 is a research-focused model designed for precise code and math generation, making it ideal for structured technical workflows.' },
        { name: 'Cohere R+', value: 'cohere', provider: 'Cohere', image: 'assets/logos/cohere.svg', blurb: 'Cohere R+ focuses on enterprise-friendly NLP with strong summarization, retrieval-augmented generation, and grounded output quality.' }
      ]
    },
    {
      id: 'video',
      title: 'Video & Image Models',
      subtitle: 'Generate clips and images for storytelling and creative workflows.',
      models: [
        { name: 'Veo 3.1', value: 'veo31', provider: 'Google', image: 'assets/logos/google.svg', blurb: 'Cinematic text-to-video generation with smooth motion and strong scene coherence.' },
        { name: 'Nova Reel 1.1', value: 'novareel11', provider: 'Amazon', image: 'assets/logos/amazon.svg', blurb: 'Nova Reel 1.1 is optimized for long-form prompt-to-video generation with optional image-guided scene control and server-managed Bedrock async rendering.' },
        { name: 'Stability 3.5', value: 'stability35l', provider: 'Stability AI', image: 'assets/stability_dark.svg', blurb: 'High-fidelity prompt-to-image model with strong detail and reliable visual consistency.' },
        { name: 'CogX', value: 'cogx', provider: 'Zhipu', image: 'assets/logos/zai.svg', blurb: 'CogX is optimized for fast, prompt-driven motion generation and iterative creative video workflows.' },
        { name: 'Gemini Pro Image', value: 'geminiproimage', provider: 'Google', image: 'assets/logos/google.svg', blurb: 'Gemini Pro Image is built for high-quality prompt-to-image generation and visual concept exploration with strong instruction following.' }
      ]
    },
    {
      id: 'image',
      title: 'Image Models',
      subtitle: 'Image generation and image editing focused models.',
      models: [
        { name: 'DALL·E', value: 'dale', provider: 'OpenAI', image: 'assets/logos/openai.svg', blurb: 'Prompt-to-image model for creative generation.' },
        { name: 'Stability', value: 'stability', provider: 'Stability AI', image: 'assets/logos/iamclari2.png', blurb: 'High-speed image generation with style control.' }
      ]
    }
  ];

  selectedCategoryId = this.categories[0].id;

  private readonly pageStartByCategory: Record<string, number> = {};
  private readonly slideIndexByCategory: Record<string, number> = {};
  private categoryCardStart = 0;

  private readonly modelValueAliases: Record<string, string> = {
    palmyra: 'palmx',
    scoutinstruct: 'scout',
    glm46v: 'zai',
    haiku: 'haiku45',
    'haiku4.5': 'haiku45',
    chatgpt: 'chatgpt54',
    chatgpt52: 'chatgpt54',
    llama4: 'maverick',
    glm: 'glm5'
  };

  private readonly modelShortBlurbs: Record<string, string> = {
    opus46: 'Frontier Claude for deep reasoning, coding, and long-context synthesis.',
    chatgpt5: 'Fast GPT-5.2 route for strong general chat and coding tasks.',
    chatgpt54: 'Flagship OpenAI reasoning with reliable instruction-following.',
    minichatgpt54: 'Lower-latency 5.4 variant for quick drafts and iteration.',
    codestralto: 'Code-specialized Mistral model for implementation and refactors.',
    opus45: 'Claude Opus 4.5 for premium depth in complex prompts.',
    sonnet45: 'Balanced Claude model for writing and technical reasoning.',
    palmx: 'Enterprise-grade long-context model with structured output.',
    gemini: 'Broad Google reasoning model for fast multimodal assistance.',
    haiku45: 'Fast Claude model for concise responses and vision+text prompts.',
    pixtralimage: 'Vision-first model tuned for image-grounded reasoning.',
    deepv3: 'Strong technical reasoning with efficient long-context handling.',
    maverick: 'Open-weight Llama 4 flagship for flexible deployments.',
    commandLight: 'Low-latency enterprise model for high-throughput chat.',
    deepseek: 'Reasoning-first model for math, code, and logic workflows.',
    cohere: 'Enterprise NLP model with grounded, structured responses.',
    scout: 'Fast Llama 4 variant for lightweight practical assistant tasks.',
    jamba: 'Long-context AI21 model for workflow-heavy enterprise prompts.',
    commandRInstruct: 'Instruction-tuned Command R model on Bedrock.',
    llama31Instruct: 'Instruction-tuned Meta Llama model on Bedrock.',
    novalite: 'Fast AWS Nova Lite for efficient assistant tasks.',
    novapro: 'AWS Nova Pro for high-quality enterprise assistant output.',
    glm5: 'Zhipu GLM 5 for multilingual reasoning, structured answers, and long-form clarity.'
    ,
    qwen: 'Qwen 3 Coder for practical coding tasks, debugging, and structured technical responses.'
  };

  constructor(
    private router: Router,
    private dialog: MatDialog
  ) {
    this.syncFirstThreeCardsWithCatalog();
  }

  get selectedCategory(): GardenCategory {
    return this.categories.find((category) => category.id === this.selectedCategoryId) || this.categories[0];
  }

  selectCategory(categoryId: string): void {
    this.selectedCategoryId = categoryId;
  }

  /** Cohere Command Light — same visual as AI catalog (⌘ + Instruct). */
  isCommandLightGardenModel(model: GardenModel): boolean {
    const v = String(model?.value || '').trim().toLowerCase();
    return v === 'commandlight';
  }

  gardenModelAriaLabel(model: GardenModel): string {
    return this.isCommandLightGardenModel(model) ? 'Command Instruct' : model.name;
  }

  get visibleCategories(): GardenCategory[] {
    return this.categories.slice(this.categoryCardStart, this.categoryCardStart + this.categoryCardsPerView);
  }

  hasPreviousCategoryCards(): boolean {
    return this.categoryCardStart > 0;
  }

  hasNextCategoryCards(): boolean {
    return this.categoryCardStart + this.categoryCardsPerView < this.categories.length;
  }

  showNextCategoryCards(): void {
    if (!this.hasNextCategoryCards()) {
      return;
    }

    this.categoryCardStart += 1;
  }

  showPreviousCategoryCards(): void {
    if (!this.hasPreviousCategoryCards()) {
      return;
    }

    this.categoryCardStart = Math.max(0, this.categoryCardStart - 1);
  }

  getVisibleModels(category: GardenCategory): GardenModel[] {
    return category.models;
  }

  hasNextPage(category: GardenCategory): boolean {
    return this.canSlideNext(category);
  }

  hasPreviousPage(category: GardenCategory): boolean {
    return this.canSlidePrevious(category);
  }

  goToNextPage(category: GardenCategory): void {
    this.slideNext(category);
  }

  goToPreviousPage(category: GardenCategory): void {
    this.slidePrevious(category);
  }

  canSlideNext(category: GardenCategory): boolean {
    return this.getSlideIndex(category.id) < this.getMaxSlideIndex(category);
  }

  canSlidePrevious(category: GardenCategory): boolean {
    return this.getSlideIndex(category.id) > 0;
  }

  slideNext(category: GardenCategory): void {
    const maxSlideIndex = this.getMaxSlideIndex(category);
    this.slideIndexByCategory[category.id] = Math.min(this.getSlideIndex(category.id) + 1, maxSlideIndex);
  }

  slidePrevious(category: GardenCategory): void {
    this.slideIndexByCategory[category.id] = Math.max(this.getSlideIndex(category.id) - 1, 0);
  }

  getSliderTransform(category: GardenCategory): string {
    return `translateX(calc(-${this.getSlideIndex(category.id)} * (31%)))`;
  }

  openModel(model: GardenModel): void {
    const resolvedModelValue = this.resolveClariChatModelValue(model.value);
    if (this.isStabilityModel(model)) {
      this.router.navigate(['/ai/images'], {
        queryParams: {
          newChat: 'true',
          selectedModel: resolvedModelValue,
          selectedModelName: model.name
        }
      });
      return;
    }

    this.router.navigate(['/ai/clarity'], {
      queryParams: {
        newChat: 'true',
        selectedModel: resolvedModelValue,
        selectedModelName: model.name
      }
    });
  }

  shouldShowChatButton(category: GardenCategory, model: GardenModel): boolean {
    if (this.isStabilityModel(model)) {
      return true;
    }
    return category.id !== 'video' && category.id !== 'image';
  }

  openModelBio(model: GardenModel): void {
    const resolvedValue = this.resolveClariChatModelValue(model.value);
    const key = String(resolvedValue || '').trim().toLowerCase();
    const targetComponent = this.modelDialogMap[key];

    if (targetComponent) {
      this.dialog.open(targetComponent, {
        width: '760px',
        maxWidth: '95vw',
        autoFocus: false
      });
      return;
    }

    this.dialog.open(ModelBioDetailDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      autoFocus: false,
      data: {
        name: model.name,
        provider: model.provider,
        modelId: resolvedValue,
        blurb: model.blurb
      }
    });
  }

  private resolveClariChatModelValue(modelValue: string): string {
    const rawValue = String(modelValue || '').trim();
    if (!rawValue) {
      return 'deepv3';
    }

    return this.modelValueAliases[rawValue.toLowerCase()] || rawValue;
  }

  private isStabilityModel(model: GardenModel): boolean {
    const normalized = this.resolveClariChatModelValue(model?.value || '').toLowerCase();
    return normalized === 'stability35l' || normalized === 'stability';
  }

  private syncFirstThreeCardsWithCatalog(): void {
    const catalogModels = getClariChatModelsFlat();
    const firstThree = this.categories.slice(0, 3);
    const existingIds = new Set(firstThree.flatMap((category) => category.models.map((model) => model.value)));
    const missing = catalogModels.filter((model) => !existingIds.has(model.id));

    missing.forEach((model) => {
      const target = this.pickTargetCategoryForModel(model.id);
      target.models.push({
        name: model.name,
        value: model.id,
        provider: this.providerForModelId(model.id),
        image: this.imageForModelId(model.id),
        blurb: this.modelShortBlurbs[model.id] || `${model.name} is available in Clari Chat for production workflows.`
      });
    });
  }

  private pickTargetCategoryForModel(modelId: string): GardenCategory {
    const id = String(modelId || '').toLowerCase();
    const visionCard = this.categories[0];
    const reasoningCard = this.categories[1];
    const instructCard = this.categories[2];

    if (['haiku45', 'pixtralimage', 'gemini', 'gemini3flash', 'chatgpt54', 'chatgpt5', 'minichatgpt54'].includes(id)) {
      return visionCard;
    }
    if (
      id.endsWith('instruct') ||
      ['scout', 'scoutinstruct', 'commandlight', 'cohere', 'jamba', 'mistrallarge'].includes(id)
    ) {
      return instructCard;
    }
    return reasoningCard;
  }

  private providerForModelId(modelId: string): string {
    const vendor = CLARI_CHAT_MODEL_VENDOR_BY_ID[String(modelId || '').toLowerCase()] || '';
    const byVendor: Record<string, string> = {
      anthropic: 'Anthropic',
      openai: 'OpenAI',
      google: 'Google',
      mistral: 'Mistral',
      writer: 'Writer',
      deepseek: 'DeepSeek',
      cohere: 'Cohere',
      meta: 'Meta',
      ai21: 'AI21 Labs',
      amazon: 'Amazon',
      zai: 'Zhipu AI',
      qwen: 'Qwen'
    };
    return byVendor[vendor] || 'AI Model';
  }

  private imageForModelId(modelId: string): string {
    const vendor = CLARI_CHAT_MODEL_VENDOR_BY_ID[String(modelId || '').toLowerCase()] || '';
    if (vendor === 'writer') return 'assets/logos/x5-baby.png';
    if (vendor === 'cohere') return 'assets/r.svg';
    if (vendor === 'ai21') return 'assets/logos/ai21.png';
    return vendor ? `assets/logos/${vendor}.svg` : 'assets/logos/iamclari2.png';
  }

  private getPageStart(categoryId: string): number {
    return this.pageStartByCategory[categoryId] || 0;
  }

  private getSlideIndex(categoryId: string): number {
    return this.slideIndexByCategory[categoryId] || 0;
  }

  private getMaxPageStart(category: GardenCategory): number {
    return Math.max(0, Math.floor((category.models.length - 1) / 4) * 4);
  }

  private getMaxSlideIndex(category: GardenCategory): number {
    return Math.max(0, this.getVisibleModels(category).length - 3);
  }
}
